import { Button } from '@components/core-ui/form-elements/buttons/Button';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { CheckBox } from '@components/core-ui/form-elements/CheckBox';
import { softValidate } from '@components/Files/DropZone/helpers/softValidate';
import { useAPI } from '@providers/useAPI';
import { JobsService } from 'app/API';
import { EACCEPTED_DOCUMENT_FORMATS } from 'app/types/enums/EACCEPTED_DOCUMENT_FORMATS';
import React, { useCallback, useRef, useState } from 'react';
import toast from 'react-hot-toast';

import { Wrapper } from './styles/Wrapper';

export { Wrapper };

type TAttachmentType = 'GENERAL' | 'NOTICE';

type TPendingNoticeFile = {
  id: string;
  name: string;
  isPdf: boolean;
};

const isPdfFile = (file: File) => {
  return file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
};

export type TProps = {
  accept?: string;
  multiple?: boolean;
  onFiles?: (files: File[]) => void;
  maxSizeBytes?: number;
  minSizeBytes?: number;
  className?: string;
  jobId: string;
  organizationId: string;
  noticeExtractorEnabled: boolean;
  reload: () => void;
};

export const UploadAttachmentsBlock: React.FC<TProps> = ({
  accept = EACCEPTED_DOCUMENT_FORMATS.join(', '),
  multiple = true,
  onFiles,
  maxSizeBytes,
  minSizeBytes,
  className,
  jobId,
  organizationId,
  noticeExtractorEnabled,
  reload,
}) => {
  const { call } = useAPI();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const noticeSelectionResolveRef = useRef<((value: Record<string, TAttachmentType>) => void) | null>(null);
  const [isOver, setOver] = useState(false);
  const [isUploading, setUploading] = useState(false);
  const [noticeModalFiles, setNoticeModalFiles] = useState<TPendingNoticeFile[]>([]);
  const [noticeSelections, setNoticeSelections] = useState<Record<string, boolean>>({});

  const resolveNoticeSelections = useCallback(
    (files: File[]) => {
      if (!noticeExtractorEnabled) {
        return Promise.resolve(
          files.reduce<Record<string, TAttachmentType>>((acc, file) => {
            acc[file.name] = 'GENERAL';
            return acc;
          }, {}),
        );
      }

      const modalFiles = files.map((file) => ({
        id: file.name,
        name: file.name,
        isPdf: isPdfFile(file),
      }));

      setNoticeModalFiles(modalFiles);
      setNoticeSelections(
        modalFiles.reduce<Record<string, boolean>>((acc, file) => {
          acc[file.id] = file.isPdf;
          return acc;
        }, {}),
      );

      return new Promise<Record<string, TAttachmentType>>((resolve) => {
        noticeSelectionResolveRef.current = resolve;
      });
    },
    [noticeExtractorEnabled],
  );

  const confirmNoticeSelections = useCallback(() => {
    const attachmentTypes = noticeModalFiles.reduce<Record<string, TAttachmentType>>((acc, file) => {
      acc[file.name] = file.isPdf && noticeSelections[file.id] ? 'NOTICE' : 'GENERAL';
      return acc;
    }, {});

    noticeSelectionResolveRef.current?.(attachmentTypes);
    noticeSelectionResolveRef.current = null;
    setNoticeModalFiles([]);
    setNoticeSelections({});
  }, [noticeModalFiles, noticeSelections]);

  const uploadAttachment = useCallback(
    async (file: File) => {
      const { url } = await call(() =>
        JobsService.getJobAttachmentUploadLink({
          jobId,
          file: file.name,
          oid: organizationId,
        }),
      );

      const response = await fetch(String(url), {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type || 'application/octet-stream',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to upload file: ${file.name}`);
      }

      const attachmentTypes = await resolveNoticeSelections([file]);
      const attachmentType = attachmentTypes[file.name] ?? 'GENERAL';
      const attachment = await call(() =>
        JobsService.putAttachment({
          jobId,
          file: file.name,
          oid: organizationId,
          attachmentType,
        }),
      );
      if (attachmentType === 'NOTICE' && attachment?.id) {
        toast.success(`Notice extraction started for ${file.name}`);
      }
    },
    [call, jobId, organizationId, resolveNoticeSelections],
  );

  const handleFiles = useCallback(
    async (list: FileList | null) => {
      if (!list) {
        return;
      }

      const files = Array.from(list);
      const validatedFiles = softValidate(files, accept, minSizeBytes, maxSizeBytes);

      if (!validatedFiles.length) {
        return;
      }

      onFiles?.(validatedFiles);

      try {
        setUploading(true);
        for await (const file of validatedFiles) {
          try {
            const toastId = toast.loading(`Uploading ${file.name}...`, { id: file.name });
            await uploadAttachment(file);
            toast.remove(toastId);
            toast.success(`Successfully uploaded ${file.name}`);
          } catch {
            toast.error(`Failed to upload ${file.name}`, { id: file.name });
          }
        }
      } finally {
        reload();
        setUploading(false);
      }
    },
    [accept, minSizeBytes, maxSizeBytes, onFiles, uploadAttachment, reload],
  );

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setOver(false);
      handleFiles(e.dataTransfer.files);
    },
    [handleFiles],
  );

  const onBrowse = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      handleFiles(e.target.files);
      if (inputRef.current) {
        inputRef.current.value = '';
      }
    },
    [handleFiles],
  );

  if (!jobId) {
    return null;
  }

  return (
    <>
      <Wrapper
        className={[
          'p-[15px] text-center relative overflow-hidden select-none font-[600] text-[12px] leading-[18px]',
          isOver
            ? 'ring-2 ring-[var(--color-text-muted)]/50 bg-[var(--color-info-bg)] text-[var(--color-text-muted)]'
            : 'text-[var(--color-text-subtle)]',
          isUploading ? 'opacity-60 pointer-events-none' : '',
          className || '',
        ].join(' ')}
        onDragOver={(e) => {
          e.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && inputRef.current?.click()}
      >
        <input
          ref={inputRef}
          type="file"
          className="invisible absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
          multiple={multiple}
          accept={accept}
          onChange={onBrowse}
        />

        {isUploading ? (
          'Uploading files...'
        ) : (
          <>
            Drag and drop or click <span className="underline text-[var(--color-text-muted)]">here</span> to upload your
            files
          </>
        )}
      </Wrapper>

      {noticeExtractorEnabled && noticeModalFiles.length ? (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-[color:var(--color-overlay)] px-4">
          <div className="w-full max-w-[520px] rounded-[8px] border border-border bg-surface p-5 shadow-[0_12px_32px_var(--color-shadow)]">
            <h3 className="m-0 text-[16px] font-bold leading-6 text-foreground">Notice attachments</h3>
            <p className="mb-4 mt-2 text-[13px] leading-[18px] text-foreground-muted">
              PDF attachments are selected as notice files by default. Other files cannot be marked as notice.
            </p>
            <div className="grid max-h-[320px] gap-2 overflow-auto pr-1">
              {noticeModalFiles.map((file) => (
                <label
                  className={[
                    'flex items-center gap-3 rounded-[8px] border border-border bg-surface-muted p-3 text-[13px] leading-[18px] text-foreground',
                    file.isPdf ? 'cursor-pointer' : 'cursor-not-allowed opacity-55',
                  ].join(' ')}
                  key={file.id}
                >
                  <CheckBox
                    name={`notice-${file.id}`}
                    checked={file.isPdf && !!noticeSelections[file.id]}
                    disabled={!file.isPdf}
                    onChange={(checked) =>
                      file.isPdf && setNoticeSelections((prev) => ({ ...prev, [file.id]: checked }))
                    }
                  />
                  <span className="min-w-0 flex-1 break-words">{file.name}</span>
                  <span className="text-[12px] font-semibold uppercase text-foreground-muted">
                    {file.isPdf ? 'Notice' : 'Not PDF'}
                  </span>
                </label>
              ))}
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <OutlinedButton
                onClick={() =>
                  setNoticeSelections(
                    noticeModalFiles.reduce<Record<string, boolean>>((acc, file) => {
                      acc[file.id] = false;
                      return acc;
                    }, {}),
                  )
                }
              >
                Clear all
              </OutlinedButton>
              <Button onClick={confirmNoticeSelections}>Continue</Button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};
